import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Table,
  Alert,
} from "reactstrap";

import { adsRepo } from "data/repositories/adsRepo";
import { adPlacements } from "data/admin/domain";
import { adminPaths } from "admin/paths";
import { confirmAdminAction, sortByOption, toDateLabel } from "admin/contentUtils";
import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";

const sortOptions = [
  { value: "order-asc", label: "Sıralama" },
  { value: "updated-desc", label: "Son güncellenen" },
  { value: "title-asc", label: "Başlığa göre A-Z" },
];

function placementLabel(value) {
  return adPlacements.find((item) => item.value === value)?.label || value;
}

export default function AdsList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState(null);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("active");
  const [placementFilter, setPlacementFilter] = useState("");
  const [sortBy, setSortBy] = useState("order-asc");
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      adsRepo.resetFirebaseAvailability();
      const next = await adsRepo.list({ includeDeleted: true });
      setItems(next);
      const warning = adsRepo.getFirebaseWarning();
      if (warning) {
        setFlash({ color: "warning", message: warning });
      }
    } catch (loadError) {
      setError(loadError?.message || "Reklamlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!location.state?.status) return;
    setFlash(location.state.status);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = items.filter((item) => {
      if (stateFilter === "active" && item.deletedAt) return false;
      if (stateFilter === "archived" && !item.deletedAt) return false;
      if (placementFilter && item.placement !== placementFilter && item.placement !== "all") return false;
      if (!query) return true;
      return [item.id, item.title, item.subtitle, item.placement, item.linkUrl]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });

    return sortByOption(filtered, sortBy, {
      "order-asc": (left, right) => (left.sortOrder || 0) - (right.sortOrder || 0),
    });
  }, [items, placementFilter, search, sortBy, stateFilter]);

  const onArchive = async (item) => {
    if (!confirmAdminAction(item.title)) return;
    try {
      await adsRepo.remove(item.id);
      setFlash({ color: "success", message: `${item.title} arşive alındı.` });
      load();
    } catch (archiveError) {
      setFlash({ color: "danger", message: archiveError?.message || "Reklam arşivlenemedi." });
    }
  };

  const onRestore = async (item) => {
    try {
      await adsRepo.restore(item.id);
      setFlash({ color: "success", message: `${item.title} tekrar yayına alındı.` });
      load();
    } catch (restoreError) {
      setFlash({ color: "danger", message: restoreError?.message || "Reklam geri alınamadı." });
    }
  };

  const onSeed = async () => {
    setSeeding(true);
    try {
      const result = await adsRepo.seedDefaults();
      setFlash({
        color: "success",
        message: result.seeded
          ? `${result.count} varsayılan reklam Firebase'e yüklendi.`
          : `Koleksiyon zaten dolu (${result.count} kayıt).`,
      });
      await load();
    } catch (seedError) {
      setFlash({ color: "danger", message: seedError?.message || "Seed başarısız." });
    } finally {
      setSeeding(false);
    }
  };

  return (
    <>
      <AdminPageHeader
        gradient="warning"
        title="Reklam Yönetimi"
        description="Canlı Firebase: mobil Keşfet / Karadeniz snap slider / Fırsatlar / İşletmeler reklamları buradan yönetilir."
        primaryTo={{ tag: Link, to: `/admin${adminPaths.content.ads.create}` }}
        primaryLabel="Yeni reklam"
      />

      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0">
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                  <h3 className="mb-0">Liste</h3>
                  <div>
                    <Badge color={adsRepo.usesFirebase() ? "success" : "warning"} pill className="mr-2">
                      {adsRepo.usesFirebase() ? "Firebase bağlı" : "Yerel depolama"}
                    </Badge>
                    {!adsRepo.usesFirebase() ? (
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        type="button"
                        className="mr-2"
                        onClick={async () => {
                          adsRepo.resetFirebaseAvailability();
                          await load();
                          if (adsRepo.getFirebaseWarning()) {
                            setFlash({ color: "warning", message: adsRepo.getFirebaseWarning() });
                          } else if (adsRepo.usesFirebase()) {
                            setFlash({ color: "success", message: "Firebase bağlantısı yeniden denendi." });
                          }
                        }}
                      >
                        Firebase’i yeniden dene
                      </Button>
                    ) : null}
                    <Button color="info" outline size="sm" type="button" disabled={seeding} onClick={onSeed}>
                      {seeding ? "Yükleniyor..." : "Varsayılanları yükle"}
                    </Button>
                  </div>
                </div>
                {adsRepo.getFirebaseWarning() ? (
                  <Alert color="warning" className="mb-3">
                    <strong>Firebase izinleri:</strong> {adsRepo.getFirebaseWarning()}
                    <div className="small mt-2 mb-0">
                      Console’da proje <code>destinationblacksea-6d8b6</code> → Firestore Rules içine{" "}
                      <code>match /ads/{"{adId}"} {"{ allow read, write: if true; }"}</code> ekleyip Publish edin.
                      Storage Rules için de <code>ads/{"{adId}"}/{"{fileName}"}</code> yazmaya izin verin.
                    </div>
                  </Alert>
                ) : null}
                <AdminListControls
                  search={search}
                  onSearch={setSearch}
                  searchPlaceholder="Başlık, yerleştirme veya link ara"
                  filters={[
                    {
                      key: "state",
                      label: "Kayıt durumu",
                      value: stateFilter,
                      onChange: setStateFilter,
                      options: [
                        { value: "active", label: "Aktif" },
                        { value: "archived", label: "Arşiv" },
                        { value: "all", label: "Tümü" },
                      ],
                    },
                    {
                      key: "placement",
                      label: "Ekran",
                      value: placementFilter,
                      onChange: setPlacementFilter,
                      options: [{ value: "", label: "Tüm ekranlar" }, ...adPlacements.map((p) => ({ value: p.value, label: p.label }))],
                    },
                  ]}
                  sortValue={sortBy}
                  onSortChange={setSortBy}
                  sortOptions={sortOptions}
                  onReset={() => {
                    setSearch("");
                    setStateFilter("active");
                    setPlacementFilter("");
                    setSortBy("order-asc");
                  }}
                  resultCount={visibleItems.length}
                  totalCount={items.length}
                />
              </CardHeader>
              <CardBody>
                <AdminStateBlock
                  flash={flash}
                  onDismissFlash={() => setFlash(null)}
                  error={error}
                  onDismissError={() => setError("")}
                  loading={loading}
                  spinnerColor="warning"
                  isEmpty={!visibleItems.length}
                  emptyTitle="Reklam bulunamadı"
                  emptyDescription="Yeni reklam ekleyin veya varsayılanları Firebase'e yükleyin."
                  emptyIcon="fas fa-ad"
                >
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        <th>Görsel</th>
                        <th>Reklam</th>
                        <th>Ekran</th>
                        <th>Sıra</th>
                        <th>Durum</th>
                        <th>Güncellendi</th>
                        <th className="text-right">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleItems.map((item) => (
                        <tr key={item.id}>
                          <td style={{ width: 88 }}>
                            {item.imageUrl ? (
                              <img
                                src={item.imageUrl}
                                alt={item.title}
                                style={{ width: 72, height: 44, objectFit: "cover", borderRadius: 6 }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: 72,
                                  height: 44,
                                  borderRadius: 6,
                                  background: item.backgroundColor || "#1B4332",
                                }}
                              />
                            )}
                          </td>
                          <td>
                            <div className="font-weight-bold">{item.title}</div>
                            <div className="small text-muted">{item.subtitle}</div>
                          </td>
                          <td>
                            <Badge color="info" pill>
                              {placementLabel(item.placement)}
                            </Badge>
                          </td>
                          <td>{item.sortOrder}</td>
                          <td>
                            <Badge color={item.deletedAt ? "warning" : "success"} pill>
                              {item.deletedAt ? "Arşivde" : "Aktif"}
                            </Badge>
                          </td>
                          <td>{toDateLabel(item.updatedAt)}</td>
                          <td className="text-right">
                            <Button
                              tag={Link}
                              to={`/admin${adminPaths.content.ads.edit(item.id)}`}
                              color="primary"
                              size="sm"
                              className="mr-2"
                            >
                              Düzenle
                            </Button>
                            {item.deletedAt ? (
                              <Button color="success" outline size="sm" onClick={() => onRestore(item)}>
                                Geri al
                              </Button>
                            ) : (
                              <Button color="danger" size="sm" onClick={() => onArchive(item)}>
                                Arşivle
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </AdminStateBlock>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
